import { Fragment } from 'react';
import { Paragraph, Text } from '@hungryroot/ramen';
import { type SectionListData } from '../../components/SectionList/utils';
import styles from '../../components/SectionList/SectionList.module.css';

// Ids and anchor links generated by getSectionId function in '../../components/SectionList/utils
export const privacySections: SectionListData = {
  personalInformation: {
    title: 'Personal Information We May Collect',
    body: (
      <Fragment key="categories">
        <Paragraph className={styles['margin-bottom']}>
          Personal information (that is, information that identifies you as an
          individual or relates to an identifiable person) that we may collect
          include:
        </Paragraph>
        <div className={styles['margin-bottom']}>
          <ul className={styles['list-indented-bullet-privacy']}>
            <li>
              <Text>First and last name</Text>
            </li>
            <li>
              <Text>Billing and shipping postal address</Text>
            </li>
            <li>
              <Text>Telephone number</Text>
            </li>
            <li>
              <Text>Email address</Text>
            </li>
            <li>
              <Text>
                Demographic information and other information you provide when
                you purchase a subscription (dietary preferences or
                restrictions) or in response to any customer survey related to
                your use of Hungryroot (prescription medication use or health
                goals and results from using Hungryroot), as well as information
                about your household size and demographics
              </Text>
            </li>
            <li>
              <Text>
                Your payment card number and related payment card information
                (card brand, expiration)
              </Text>
            </li>
            <li>
              <Text>
                Browser and device information, such as operating system name
                and version, device manufacturer and model, language, Internet
                browser type and version
              </Text>
            </li>
            <li>
              <Text>App usage data</Text>
            </li>
            <li>
              <Text>Social media account handle</Text>
            </li>
            <li>
              <Text>
                Information collected through cookies, pixel tags, and other
                digital tracking technologies
              </Text>
            </li>
            <li>
              <Text>Aggregated information</Text>
            </li>
            <li>
              <Text>Internet Protocol (IP) address</Text>
            </li>
            <li>
              <Text>Account username and password</Text>
            </li>
          </ul>
        </div>
        <Paragraph className={styles['margin-bottom-large']}>
          If you submit personal information relating to other individuals (such
          as other members of your household) to us or our service providers in
          connection with your use of our Services, you represent that you have
          the authority to do so and that we may use such information in
          accordance with this Privacy Policy.
        </Paragraph>
      </Fragment>
    ),
  },
  sources: {
    title: 'How We May Collect Personal Information',
    body: (
      <Fragment key="sources">
        <Paragraph className={styles['margin-bottom']}>
          We may collect Personal Information:
        </Paragraph>
        <div className={styles['margin-bottom-large']}>
          <ul className={styles['list-indented-bullet-privacy']}>
            <li>
              <Text className={styles.italic}>
                When you register for a subscription or otherwise interact with
                our Services.
              </Text>{' '}
              <Text>
                For example, when you place an order for a subscription or
                choose to complete our voluntary quiz that helps us identify
                your dietary preferences.
              </Text>
            </li>
            <li>
              <Text className={styles.italic}>
                Automatically through your browser, device, or use of the App.
              </Text>{' '}
              <Text>
                We collect technical information automatically; for example,
                your IP address (through which we can determine your general
                location) and data about which pages you visit on the Site
                through the use of log files provided by our service providers.
              </Text>
            </li>
            <li>
              <Text className={styles.italic}>
                When you contact us with questions, comments, or for customer
                support.
              </Text>{' '}
              <Text>For example, when you request customer support.</Text>
            </li>
            <li>
              <Text className={styles.italic}>From other sources.</Text>{' '}
              <Text>
                For example, through social media and other third parties, to
                the extent permitted by applicable law. If you create an account
                using Apple Sign-In or Facebook Login, Hungryroot may collect
                customer information such as the name and email address
                associated with the third-party service.
              </Text>
            </li>
            <li>
              <Text className={styles.italic}>
                Through use of cookies, pixel tags and other digital tracking
                technologies.
              </Text>{' '}
              <Text>
                For example, browser type, time spent on the Services, pages
                visited on the Site, and to measure the success of our marketing
                campaigns.
              </Text>
            </li>
            <li>
              <Text className={styles.italic}>
                Through voluntary surveys, focus groups, video or audio
                conferences and other research-related activities, including
                information that may be considered sensitive (such as dietary
                preferences or restrictions, prescription medication use, or
                health goals and outcomes from using Hungryroot).
              </Text>{' '}
              <Text>
                including information that may be considered sensitive (such as
                dietary preferences or restrictions, or health goals and
                outcomes from using Hungryroot).
              </Text>
            </li>
            <li>
              <Text className={styles.italic}>By aggregating information.</Text>{' '}
              <Text>
                Note that such information does not personally identify you.
              </Text>
            </li>
          </ul>
        </div>
      </Fragment>
    ),
  },
  uses: {
    title: 'How We May Use Your Personal Information',
    body: (
      <>
        <Paragraph className={styles['margin-bottom']}>
          The business and commercial purposes for which the elements and
          categories of personal information set forth above may be used or
          disclosed are to:
        </Paragraph>
        <div className={styles['margin-bottom-large']}>
          <ul className={styles['list-indented-bullet-privacy']}>
            <li>
              <Text>
                Deliver our services and carry out obligations relating to your
                periodic renewing subscription with us. For example, when you
                sign up to receive groceries through our Services, you enter
                into a subscription with us to make these grocery deliveries on
                your behalf. We must process your personal information to fulfil
                your orders.
              </Text>
            </li>
            <li>
              <Text>
                Provide, maintain, personalize, optimize, and improve the
                Services, including research and analytics regarding use of the
                Services, to provide a more personalized experience, or to
                remember you when you leave and return to the Services.
              </Text>
            </li>
            <li>
              <Text>
                Deliver our newsletter to you, whether or not you are a
                customer.
              </Text>
            </li>
            <li>
              <Text>
                Respond to your inquiries or requests for customer support.
              </Text>
            </li>
            <li>
              <Text>
                Effectuate billing and payment for your purchase of the
                Services.
              </Text>
            </li>
            <li>
              <Text>Conduct identification and authentication checks.</Text>
            </li>
            <li>
              <Text>Contact prospective customers.</Text>
            </li>
            <li>
              <Text>Conduct general research and aggregate reporting.</Text>
            </li>
            <li>
              <Text>
                Collect, process, and retain your personal information where
                required by law (for example, to fulfill our regulatory
                obligations).
              </Text>
            </li>
            <li>
              <Text>
                Provide you with more relevant content in marketing,
                promotional, or other communications to which you may be
                subscribed.
              </Text>
            </li>
            <li>
              <Text>
                Detect, investigate, and prevent activities that may violate our
                policies or be fraudulent or illegal.
              </Text>
            </li>
            <li>
              <Text>
                Provide you with relevant information about our products and
                services, including to help you learn about other services that
                we offer.
              </Text>
            </li>
          </ul>
        </div>
      </>
    ),
  },
  thirdParties: {
    title: 'Third Parties With Whom Personal Information May Be Disclosed',
    body: (
      <>
        <Paragraph className={styles['margin-bottom']}>
          Hungryroot uses other companies and people to perform tasks on our
          behalf, and we need to share your information with them to provide
          products and services to you. Examples include but are not limited to
          fulfilling orders, processing payments, handling billing disputes and
          collections, analyzing data, providing marketing assistance, providing
          you with information about products and services you may want to
          purchase from us or our partners, providing customer service, and
          conducting or gathering research-related information. We may disclose
          your personal information to such entities, as set forth below. Note
          that such disclosure may be based on your instruction (e.g., through
          your use of the Services, a social media widget, or third-party
          integration, such as when you sign in to your Hungryroot account
          through Apple or Facebook). Data obtained through the short code
          program will not be shared with any third-parties for their marketing
          reasons/purposes.
        </Paragraph>
        <Paragraph className={styles['margin-bottom']}>
          Note that this Privacy Policy does not apply to the practices of third
          parties that Hungryroot does not own or control, or individuals that
          Hungryroot does not employ or manage; you are subject to the privacy
          policies of those third parties. Hungryroot is not responsible for the
          privacy or security practices of other websites on the Internet, even
          those linked to or from the Services. We encourage you to review the
          privacy policy of any entity whose products or services you use
          through the Services.
        </Paragraph>
        <div className={styles['margin-bottom-large']}>
          <ul className={styles['list-indented-bullet-privacy']}>
            <li>
              <Text>
                Third-party service providers who provide services such as
                website hosting, chat features, advertising technology services,
                payment processing (e.g., Stripe), order fulfillment & shipping
                providers (e.g. Dispatch), information technology and related
                infrastructure provision, customer support, analytics services,
                email delivery, auditing, and other services.
              </Text>
            </li>
            <li>
              <Text>Third parties authorized by you.</Text>
            </li>
            <li>
              <Text>
                Any surviving entity or successors-in-interest in the event of
                any reorganization, merger, sale, joint venture, assignment,
                transfer, or other disposition of all or any portion of our
                business, assets, or stock (including in connection with any
                bankruptcy or similar proceedings) and, in each case, any due
                diligence relating thereto.
              </Text>
            </li>
            <li>
              <Text>
                Business partners that display our ads on their digital
                properties, or to whose digital properties the Services link,
                including those partners that help us perform targeted
                advertising.
              </Text>
            </li>
            <li>
              <Text>
                Advertisers and advertising networks to select and serve
                relevant adverts to you and others. The information shared may
                include device-level information and analytics.
              </Text>
            </li>
            <li>
              <Text>
                Analytics and search engine providers that assist us in the
                improvement and optimization of our site.
              </Text>
            </li>
            <li>
              <Text>
                As we believe necessary: (i) under applicable law; (ii)
                investigate or take action regarding illegal activities,
                suspected fraud, or violations of Hungryroot's Terms of Use;
                (iii) to protect our rights, privacy, safety, or property, or
                the safety or property of yours or others; (iv) to allow us to
                pursue available remedies or limit our damages; and (v) to
                respond or comply with lawful requests from courts, law
                enforcement agencies, regulatory agencies, and other government
                authorities, which may include authorities outside your state or
                country of residence.
              </Text>
            </li>
          </ul>
        </div>
      </>
    ),
  },
  marketing: {
    title: 'Marketing Communications',
    body: (
      <Fragment key="marketing">
        <Paragraph className={styles['margin-bottom']}>
          If you no longer wish to receive marketing-related emails, such as our
          newsletter, you may opt out by following the instructions contained in
          each such email, or by emailing your request to{' '}
          <a className={styles.link} href="mailto:help@hungryroot.com">
            help@hungryroot.com
          </a>
          . Some messages from Hungryroot are Services-related, such as
          confirmation of your order or correspondence with the Hungryroot
          support team. You may not opt out of such emails. You may also opt out
          of push notifications from the Hungryroot app by updating your device
          settings accordingly.
        </Paragraph>
        <Paragraph className={styles['margin-bottom-large']}>
          With your consent, we may also send you SMS messages about your order
          or to provide other information to you. You may opt out of receiving
          SMS messages by replying “STOP” to the sender at any time. Please note
          that our partners, including our shipping partners, may also contact
          you directly using SMS regarding your order. These messages are
          separate from any SMS messages Hungryroot sends to you.
        </Paragraph>
      </Fragment>
    ),
  },
  security: {
    title: 'Security',
    body: (
      <Fragment key="security">
        <Paragraph className={styles['margin-bottom-large']}>
          We take steps to ensure that your information is treated securely and
          in accordance with this Privacy Policy. We also implement reasonable
          measures to help protect information about you from loss, theft,
          misuse and unauthorized access, disclosure, alteration, and
          destruction. For example, we may use network safeguards such as
          firewalls and data encryption, Intrusion Detection Systems (IDS) and
          Intrusion Prevention Systems (IPS), enforce physical access controls,
          and authorize access to personal data only for those people who
          require access to fulfill their job responsibilities. Unfortunately,
          no method of transmission over the internet or method of electronic
          storage is 100% secure. You are responsible for keeping confidential
          any passwords you use to access our Services and your email account,
          and for keeping your SMS destination secure. We ask you not to share
          this password with anyone else and not to use this password for other
          services or products. To the fullest extent permitted by applicable
          law, we do not accept liability for any unauthorized disclosure.
        </Paragraph>
      </Fragment>
    ),
  },
  eighteen: {
    title: 'Persons Under 18 Years of Age',
    body: (
      <Fragment key="eighteen">
        <Paragraph className={styles['margin-bottom-large']}>
          We do not knowingly collect the personal information of children under
          eighteen (18) years of age. If we learn that we have collected such
          personal information, we will take reasonable steps to remove such
          information from our database.
        </Paragraph>
      </Fragment>
    ),
  },
  digitalTracking: {
    title: 'Digital Tracking Technologies',
    body: (
      <Fragment key="digital-tracking">
        <Paragraph className={styles['margin-bottom']}>
          Hungryroot uses a variety of digital tracking technologies to help us
          better understand how people use our Services and facilitate ad
          targeting by third parties for first and third party products or
          services. For example, Hungryroot uses at least the following digital
          tracking technologies: Google Analytics, Heap, Braze, and Segment.
          Your activities with our Services may be shared with these companies.
          Hungryroot also partners with third-party services that may use
          tracking technologies, such as browser cookies or Flash cookies, to
          provide certain services or features. These technologies allow, for
          example, a partner to recognize your computer each time you visit
          Hungryroot.
        </Paragraph>
        <Paragraph className={styles['margin-bottom-large']}>
          We also use{' '}
          <a
            href="https://support.google.com/My-Ad-Center-Help/answer/12155451?visit_id=638229040258760354-315155576&rd=1"
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            Google
          </a>
          ,{' '}
          <a
            href="https://www.facebook.com/help/568137493302217"
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            Facebook
          </a>
          ,{' '}
          <a
            href="https://about.instagram.com/safety"
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            Instagram
          </a>
          ,{' '}
          <a
            href="https://support.tiktok.com/en/account-and-privacy/personalized-ads-and-data/use-of-off-tiktok-activity-for-ad-targeting"
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            TikTok
          </a>
          , or{' '}
          <a
            href="https://help.twitter.com/en/safety-and-security/privacy-controls-for-tailored-ads"
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            Twitter
          </a>{' '}
          for advertising purposes. Please refer to these companies' linked
          websites to understand their related opt-out choices. More broadly,
          the online advertising industry also provides websites from which you
          may opt out of receiving targeted ads from data partners and other
          advertising partners that participate in self-regulatory programs. You
          can access these and learn more about targeted advertising and
          consumer choice and privacy by visiting the{' '}
          <a
            href="https://optout.networkadvertising.org/?c=1"
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            Network Advertising Initiative
          </a>{' '}
          or the{' '}
          <a
            href="https://optout.aboutads.info/?c=2&lang=EN"
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            Digital Advertising Alliance
          </a>{' '}
          (including for mobile apps at{' '}
          <a
            href="https://youradchoices.com/appchoices"
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            https://youradchoices.com/appchoices
          </a>
          ). Please note you must separately opt out in each browser and on each
          device; if you clear cache or cookies, be sure to reset your
          preferences as applicable (such as in the browser context). We are not
          responsible to the extent any such opt-outs are not honored by any
          advertising technology companies. Some internet browsers allow you to
          limit or disable the use of tracking technologies. More detailed
          information about this can be found at the browser's website or
          settings.
        </Paragraph>
      </Fragment>
    ),
  },
  dataRetention: {
    title: 'Deletion Requests',
    body: (
      <Paragraph className={styles['margin-bottom-large']}>
        You may have the right to request that we delete certain personal
        information collected and maintained about you, subject to certain
        exceptions. Deletion requests may be submitted using our online form
        available{' '}
        <a
          href="/privacy/state/"
          target="_blank"
          className={`${styles.link} ${styles.bold}`}
        >
          <span className={styles.link}>here</span>
        </a>
        . Once your request is verified and we have determined that we are
        required to delete that information in accordance with applicable law,
        we will delete your personal information accordingly. Your request to
        delete your personal information may be denied if it is necessary for us
        to retain your information under one or more of the exceptions listed in
        the State Privacy Laws, or if we are otherwise legally required to
        retain your information (e.g., to comply with law enforcement or a court
        order). Please note that a record of your deletion request may be kept
        pursuant to our legal obligations.
      </Paragraph>
    ),
  },
  doNotShare: {
    title: 'Do Not Sell or Share My Personal Information',
    body: (
      <Paragraph className={styles['margin-bottom-large']}>
        <strong>
          You may have the right to opt out of the “sale” and “sharing” of your
          personal information, as such terms are described above, including the
          processing of personal information for targeted advertising more
          generally.
        </strong>{' '}
        If you wish to opt out of these uses of your personal information, you
        may either submit a request to{' '}
        <a className={styles.link} href="mailto:help@hungryroot.com">
          help@hungryroot.com
        </a>
        , or complete our online form available{' '}
        <a
          href="https://airtable.com/shr3O0BHKqYS0uu5v"
          target="_blank"
          rel="noreferrer noopener"
          className={`${styles.link} ${styles.bold}`}
        >
          <span className={styles.link}>here</span>
        </a>{' '}
        or when you click on the “Do Not Sell or Share My Personal Information”
        link available on our homepage.
      </Paragraph>
    ),
  },
  updates: {
    title: 'Updates to this Privacy Policy',
    body: (
      <Paragraph className={styles['margin-bottom-large']}>
        Any changes we may make to this Privacy Policy will be posted on this
        page and, where appropriate, you may be notified by email, in the App,
        or other places that Hungryroot deems appropriate. Please check back
        here frequently to see any updates or changes to this Privacy Policy.
        Your continued use of our Services following the posting of any changes
        will mean that you accept the changes we have made. The “Effective Date”
        at the top of this Privacy Policy indicates the date on which this
        Privacy Policy was last updated.
      </Paragraph>
    ),
  },
  contact: {
    title: 'Contact Us',
    body: (
      <Paragraph>
        If you have questions or suggestions you can contact Hungryroot's
        support team by sending an email to{' '}
        <a className={styles.link} href="mailto:help@hungryroot.com">
          help@hungryroot.com
        </a>
        .
      </Paragraph>
    ),
  },
};
